/**
 * Earn Rules tab — manage engagement earning actions.
 *
 * Derives state from server data on every render (useMemo), not from stale
 * useState closure. Each toggle auto-saves immediately via mutation.
 *
 * Plan 177, Epic 07 — Admin Management & Analytics
 */

import { useMemo } from "react";
import { Loader2, ShoppingBag, UserPlus, Cake, Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  useEarnRules,
  useUpdateEarnRule,
  type RewardsConfigResponse,
} from "../api/rewardsAdminHooks";

interface EarnRulesTabProps {
  config: RewardsConfigResponse;
}

const EARN_ACTIONS = [
  { earn_type: "signup", label: "สมัครสมาชิก", icon: UserPlus, defaultPoints: 50 },
  { earn_type: "birthday", label: "วันเกิด", icon: Cake, defaultPoints: 100 },
  { earn_type: "referral", label: "แนะนำเพื่อน", icon: Share2, defaultPoints: 200 },
] as const;

export function EarnRulesTab({ config }: EarnRulesTabProps) {
  const earnRulesQuery = useEarnRules(true);
  const updateMutation = useUpdateEarnRule();

  // Derive merged actions from server data on every render
  const actions = useMemo(() => {
    const serverRules = earnRulesQuery.data?.rules ?? [];
    return EARN_ACTIONS.map((def) => {
      const existing = serverRules.find((r) => r.earn_type === def.earn_type);
      return {
        ...def,
        enabled: existing ? !!existing.enabled : false,
        points: existing?.points ?? def.defaultPoints,
      };
    });
  }, [earnRulesQuery.data]);

  const handleToggle = (earn_type: string, currentEnabled: boolean, points: number) => {
    updateMutation.mutate(
      {
        earn_type,
        enabled: currentEnabled ? 0 : 1,
        points,
      },
      {
        onSuccess: () => toast.success("บันทึกสำเร็จ"),
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  const handlePointsChange = (earn_type: string, enabled: boolean, points: number) => {
    if (points <= 0) return;
    updateMutation.mutate(
      {
        earn_type,
        enabled: enabled ? 1 : 0,
        points,
      },
      {
        onSuccess: () => toast.success("บันทึกสำเร็จ"),
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  if (earnRulesQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-4">
      {/* Purchase earning — always on, read-only */}
      <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
        <ShoppingBag className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">การซื้อสินค้า</p>
          <p className="text-xs text-muted-foreground">
            1 แต้มต่อ ฿{config.collection_factor} (อัตโนมัติ)
          </p>
        </div>
        <Switch checked disabled />
      </div>

      {/* Toggleable earn actions */}
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <div
            key={action.earn_type}
            className="flex items-center gap-3 rounded-lg border p-3"
          >
            <Icon className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{action.label}</p>
            </div>
            <Input
              key={`${action.earn_type}-${action.points}`}
              type="number"
              min={1}
              step={1}
              defaultValue={action.points}
              onBlur={(e) => {
                const val = Number(e.target.value);
                if (val > 0 && val !== action.points) {
                  handlePointsChange(action.earn_type, action.enabled, val);
                }
              }}
              className="w-20 text-right"
              disabled={!action.enabled}
            />
            <span className="text-xs text-muted-foreground w-10">แต้ม</span>
            <Switch
              checked={action.enabled}
              onCheckedChange={() =>
                handleToggle(action.earn_type, action.enabled, action.points)
              }
              disabled={updateMutation.isPending}
            />
            {updateMutation.isPending && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
          </div>
        );
      })}
    </div>
  );
}
